import {useMemo, useState} from "react";
import {CustomFieldProfileEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {getEntityTypeDisplayValueAsString} from "../../../util/entity-ui-util";

export type UseCustomFieldProfileDataTableFilterProps = Omit<UseDataTableFilterProps<CustomFieldProfileEntity>, 'filterConfig'>

export function useCustomFieldProfileDataTableFilter(props: UseCustomFieldProfileDataTableFilterProps): UseDataTableFilterResult<CustomFieldProfileEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [companyNameQuery, setCompanyNameQuery] = useState<string | undefined>(undefined);
  const [companyNameDebouncedQuery] = useDebouncedValue(companyNameQuery, 200);

  const [forObjectTypeQuery, setForObjectTypeQuery] = useState<string | undefined>(undefined);
  const [forObjectTypeDebouncedQuery] = useDebouncedValue(forObjectTypeQuery, 200);

  const filterConfig: DataTableFilterConfig<CustomFieldProfileEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: ({name}, query) => !name || !name.toLowerCase().includes(query.toLowerCase())
      },
      "companyName": {
        setQuery: setCompanyNameQuery,
        query: companyNameQuery,
        debouncedQuery: companyNameDebouncedQuery,
        filter: ({companyName}, query) => !companyName || !companyName.toLowerCase().includes(query.toLowerCase())
      },
      "forObjectType": {
        setQuery: setForObjectTypeQuery,
        query: forObjectTypeQuery,
        debouncedQuery: forObjectTypeDebouncedQuery,
        filter: ({forObjectType}, query) => {
          if (forObjectType) {
            const display = getEntityTypeDisplayValueAsString(forObjectType, t)
            return !forObjectType.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
          } else {
            return false
          }
        }
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    companyNameQuery,
    companyNameDebouncedQuery,
    forObjectTypeQuery,
    forObjectTypeDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
